import { observer } from 'mobx-react';
import { UnlinkOptions } from '../MasterDataPageStore';
import { RightSidebar } from '../../../layout/main/rightSidebar';
import { RouterPaths } from '../../../router';
import { Col, Container, Row } from 'react-bootstrap';
import { LinkedItem, NO_MAPPED_ID } from '../../../core/entity/mapping';
import { MAPPING_UI } from '@idexx/cfs-mapping-ui';
import { BaseSyntheticEvent, createRef, ReactElement, RefObject, SyntheticEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Trans } from 'react-i18next';
import { ClassificationEditFields } from './editable/ClassificationEditFields';
import { SpotSvg } from '@enterprise/spot';
import i18n from 'i18next';
import { DeleteWidget } from '../../../components/helpers/DeleteWidget';
import { useStores } from '../../../hooks';
import { matchPath } from 'react-router';

export const ClassificationDetailsPage = observer(function ClassificationDetailsPageComponent() {
    // Access nested stores lazily, as the class getters did.
    const { domain, ui } = useStores();
    const masterDataPageStore = domain.masterDataPageStore;
    const routerStore = ui?.router;

    const [loadingError, setLoadingError] = useState<string | undefined>(undefined);
    const refMap = useRef(new Map<string, RefObject<HTMLDivElement>>());

    const isNewClassification = Boolean(
        matchPath({ path: RouterPaths.MasterDataPages.ClassificationsMappingCreatePage, end: true }, location.pathname),
    );

    const { id, parentId } = (() => {
        const params = routerStore.params;
        return { id: String(params.id), parentId: (params.parentId || undefined) && String(params.parentId) };
    })();

    useEffect(() => {
        const loadClassification = (classificationId: string, classificationParentId?: string) => {
            masterDataPageStore
                .getClassification(String(classificationId), (classificationParentId && String(classificationParentId)) || undefined)
                .catch(() => {
                    setLoadingError(i18n.t('common:classificationDetails.loadingError'));
                });
        };

        if (!isNewClassification) {
            loadClassification(id, parentId);
        }

        if (isNewClassification && parentId) {
            loadClassification(parentId);
        }

        return () => {
            masterDataPageStore.classification = undefined;
        };
    }, [masterDataPageStore, id, parentId, isNewClassification]);

    const back = () => {
        routerStore.push(RouterPaths.MasterDataPages.ClassificationsMappingPage);
    };

    const cancelEdit = (event: SyntheticEvent) => {
        event.preventDefault();
        if (isNewClassification) {
            back();
        }
    };

    const onSuccessfulEdit = () => {
        if (isNewClassification) {
            routerStore.push(RouterPaths.MasterDataPages.ClassificationsMappingPage);
        }
    };

    const scrollHandler = (event: BaseSyntheticEvent) => {
        const letterClicked = event.target.innerText;
        const scrollRef = refMap.current.get(letterClicked);
        if (!scrollRef || !scrollRef.current) {
            return;
        }

        scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const unlink = (siteId, linkedId, pimsParentId, description) => {
        const options: UnlinkOptions = {
            mappingId: String(id),
            mappingParentId: (parentId && String(parentId)) || undefined,
            siteId,
            linkedId,
            pimsParentId,
            description,
        };
        masterDataPageStore.unlinkClassification(options);
    };

    const renderNewClassificationHeader = () => {
        const { classification } = masterDataPageStore;

        return !parentId ? (
            <Container className="header">
                <Row>
                    <Col xs={12}>
                        <span className="title">
                            <Trans i18nKey="masterData:classificationsPage.newMasterClassification">New Master Classification</Trans>
                        </span>
                    </Col>
                </Row>
            </Container>
        ) : (
            <Container className="header">
                <Row>
                    <Col xs={12}>
                        <span className="title">
                            <Trans i18nKey="masterData:classificationsPage.newMasterSubclassification">New Master Subclassification</Trans>
                        </span>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <span className="info">
                            {i18n.t('masterData:classificationsPage.classification', 'Classification')}: {classification?.data.description}
                        </span>
                    </Col>
                </Row>
            </Container>
        );
    };

    const renderHeader = () => {
        const { classification } = masterDataPageStore;

        if (isNewClassification) {
            return renderNewClassificationHeader();
        }

        if (!classification) {
            return null;
        }

        const onDeleteHandler = () => {
            masterDataPageStore.deleteClassification(classification).then((isSuccess: boolean) => {
                if (isSuccess) {
                    back();
                }
            });
        };

        return (
            <Container className="header">
                <Row>
                    <Col xs={12}>
                        <span className="title">{classification.data.description}</span>
                        <span className="id">{classification.id}</span>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <DeleteWidget automationId="delete-classification" onConfirmDelete={onDeleteHandler} />
                    </Col>
                </Row>
            </Container>
        );
    };

    const sortSitesByName = (siteIds) => {
        const siteIdName = siteIds.map((siteId) => {
            const site = masterDataPageStore.applications.find((app) => `${app.id}` === `${siteId}`);
            const siteName = site ? site.name : 'A';
            return { siteId, siteName };
        });
        siteIdName.sort((a, b) => a.siteName.localeCompare(b.siteName));
        return siteIdName;
    };

    const groupMappedBySite = (mapped: LinkedItem[]) => {
        const grouped: Map<string, LinkedItem[]> = new Map();
        mapped.forEach((mappedElement) => {
            const siteId = mappedElement.practiceId;
            const mappedForSite = grouped.has(siteId) ? grouped.get(siteId) || [] : [];
            mappedForSite.push(mappedElement);
            grouped.set(siteId, mappedForSite);
        });
        return grouped;
    };

    const getDisplayableMappingDetails = (linkedId: string, description?: string | null): ReactElement => {
        if (linkedId === NO_MAPPED_ID) {
            return <span className="name empty">{i18n.t('masterData:classificationsPage.emptySubclassification', 'Empty Subclassification')}</span>;
        }
        return (
            <>
                <span className="name">{description}</span>
                <span className="id">{linkedId}</span>
            </>
        );
    };

    const renderMappedForSiteClassifications = (siteId: string, siteName: string, mappedForSite: LinkedItem[]) => {
        const nameFirstLetter = siteName[0];
        const scrollRef: RefObject<HTMLDivElement> = createRef();
        if (!refMap.current.has(nameFirstLetter)) {
            refMap.current.set(nameFirstLetter, scrollRef);
        }

        return (
            <div className="site-mappings" key={siteId}>
                <div className="site-description" ref={scrollRef}>
                    <span className="name">{siteName}</span>
                    <span className="id float-right">{siteId}</span>
                </div>
                {mappedForSite.map((linkedItem) => {
                    const data = linkedItem.data || {};
                    const onUnlink = () => unlink(siteId, linkedItem.linkedId, linkedItem.pimsParentId, linkedItem.data?.description);
                    return (
                        <div className="mapping" key={linkedItem.linkedId}>
                            {getDisplayableMappingDetails(linkedItem.linkedId, data.description)}
                            <SpotSvg onClick={onUnlink} icon="delete" className="spot-link__icon float-right unlink-icon" />
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMappedClassifications = (sitesSortedByName, mapped: LinkedItem[]) => {
        if (mapped.length === 0) {
            return null;
        }

        const groupedBySite = groupMappedBySite(mapped);

        return sitesSortedByName.map((siteIdName) => {
            const siteId = siteIdName.siteId;
            const siteName = siteIdName.siteName;
            const mappedForSite = groupedBySite.get(siteId) || [];
            return renderMappedForSiteClassifications(siteId, siteName, mappedForSite);
        });
    };

    const renderClassifications = (mapped: LinkedItem[] = []) => {
        const uniqueSiteIds = Array.from(new Set(mapped.map((mappedItem) => mappedItem.practiceId)));
        const sitesSortedByName = sortSitesByName(uniqueSiteIds);
        const nameFirstLetters: string[] = Array.from(new Set(sitesSortedByName.map((obj) => obj.siteName[0]).map((letter) => letter.toUpperCase())));
        let clinicsCount = 0;
        if (mapped) {
            const siteIds = new Set(mapped.map(({ practiceId }) => practiceId));
            clinicsCount = siteIds.size;
        }
        return (
            <div className="body">
                <div className="content">
                    <div className="details">
                        <Trans i18nKey="common:clinics.clinicsCount" count={clinicsCount} />
                    </div>
                    {renderMappedClassifications(sitesSortedByName, mapped)}
                </div>
                <div className={classNames(MAPPING_UI.COLUMN_LETTER_BAR, 'letter-bar')}>
                    <div>
                        {nameFirstLetters &&
                            nameFirstLetters.map((letter) => (
                                <span onClick={scrollHandler} key={letter}>
                                    {letter}
                                </span>
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    const { classification, isUnlinkingClassification, isDeletingClassification, isLoadingClassification } = masterDataPageStore;
    const showLoadingSpinner = isLoadingClassification || isUnlinkingClassification || isDeletingClassification;
    const loadingText = isDeletingClassification
        ? i18n.t('common:classificationDetails.deletingClassification', 'Deleting classification...')
        : i18n.t('common:classificationDetails.loadingClassification', 'Loading classification...');

    return (
        <div className="classification-details">
            <RightSidebar
                isLoading={showLoadingSpinner}
                loadingText={loadingText}
                header={renderHeader}
                close={back}
                isSmall={true}
                modalBodyStyle={{ backgroundColor: '#ffffff' }}
            >
                <>
                    {loadingError && <span>{loadingError}</span>}
                    {!loadingError && isNewClassification && (
                        <ClassificationEditFields onCancel={cancelEdit} onSuccess={onSuccessfulEdit} parentId={parentId} />
                    )}
                    {!loadingError && !isNewClassification && classification && renderClassifications(classification.mapped)}
                </>
            </RightSidebar>
        </div>
    );
});
